// mockFidelityStates.js
// -----------------------------------------------------------------------------
// Hard-coded fixture data for the Fidelity Panel mockup. Each fixture is the
// shape that POST /api/voice-snippets/score will return (see
// docs/journal/PHASE_4D_VA_FIDELITY_GATE_DESIGN.md §6.1) plus the prompt text
// (which the panel needs to render the diff against the heard transcript).
//
// To exercise additional UX states, add a fixture here and it will appear as a
// new toggle button in the FidelityPanelDemo page.
// -----------------------------------------------------------------------------

const PROMPT_CLEAN =
  "Lock the gate. I told you to lock it twice. Do it now.";

const PROMPT_DIANA_DIRECTIVE =
  "Don't open that door. I mean it. Stay where you are.";

const PROMPT_LONG =
  "The wind cut across the field and she pulled her coat tighter, knowing the truck would not come back this side of midnight.";

/**
 * GREEN — clean read. Composite score 92. Subtle ✓ badge.
 */
const greenState = {
  label: 'GREEN · clean read',
  promptText: PROMPT_CLEAN,
  score: {
    fidelityScore: 92,
    fidelityBand: 'GREEN',
    fidelityComputedAt: '2026-05-20T20:34:12.847Z',
    fidelityProviderId: 'openai:whisper-1',
    fidelityBreakdown: {
      werScore: 100,
      werPct: 0.0,
      confScore: 88,
      minWordConfidence: -0.38,
      acousticScore: 75,
      acousticFlags: ['low_confidence_word'],
    },
    transcript: 'Lock the gate. I told you to lock it twice. Do it now.',
    transcriptDiff: [
      { type: 'match', promptWord: 'Lock', transcribedWord: 'Lock' },
      { type: 'match', promptWord: 'the', transcribedWord: 'the' },
      { type: 'match', promptWord: 'gate.', transcribedWord: 'gate.' },
      { type: 'match', promptWord: 'I', transcribedWord: 'I' },
      { type: 'match', promptWord: 'told', transcribedWord: 'told' },
      { type: 'match', promptWord: 'you', transcribedWord: 'you' },
      { type: 'match', promptWord: 'to', transcribedWord: 'to' },
      { type: 'match', promptWord: 'lock', transcribedWord: 'lock' },
      { type: 'match', promptWord: 'it', transcribedWord: 'it' },
      { type: 'match', promptWord: 'twice.', transcribedWord: 'twice.' },
      { type: 'match', promptWord: 'Do', transcribedWord: 'Do' },
      { type: 'match', promptWord: 'it', transcribedWord: 'it' },
      { type: 'match', promptWord: 'now.', transcribedWord: 'now.' },
    ],
  },
};

/**
 * AMBER — repetition stumble. Composite score 76. Reason field optional.
 */
const amberState = {
  label: 'AMBER · repetition stumble',
  promptText: PROMPT_CLEAN,
  score: {
    fidelityScore: 76,
    fidelityBand: 'AMBER',
    fidelityComputedAt: '2026-05-20T20:35:01.001Z',
    fidelityProviderId: 'openai:whisper-1',
    fidelityBreakdown: {
      werScore: 70,
      werPct: 0.07,
      confScore: 82,
      minWordConfidence: -0.46,
      acousticScore: 75,
      acousticFlags: ['low_confidence_word'],
    },
    transcript: 'Lock the lock the gate I told you to lock it twice do it now',
    transcriptDiff: [
      { type: 'match', promptWord: 'Lock', transcribedWord: 'Lock' },
      { type: 'match', promptWord: 'the', transcribedWord: 'the' },
      { type: 'ins', promptWord: null, transcribedWord: 'lock' },
      { type: 'ins', promptWord: null, transcribedWord: 'the' },
      { type: 'match', promptWord: 'gate.', transcribedWord: 'gate' },
      { type: 'match', promptWord: 'I', transcribedWord: 'I' },
      { type: 'match', promptWord: 'told', transcribedWord: 'told' },
      { type: 'match', promptWord: 'you', transcribedWord: 'you' },
      { type: 'match', promptWord: 'to', transcribedWord: 'to' },
      { type: 'match', promptWord: 'lock', transcribedWord: 'lock' },
      { type: 'match', promptWord: 'it', transcribedWord: 'it' },
      { type: 'match', promptWord: 'twice.', transcribedWord: 'twice' },
      { type: 'match', promptWord: 'Do', transcribedWord: 'do' },
      { type: 'match', promptWord: 'it', transcribedWord: 'it' },
      { type: 'match', promptWord: 'now.', transcribedWord: 'now' },
    ],
  },
};

/**
 * RED — major divergence (missing words + words swapped). Score 42. Reason required.
 */
const redState = {
  label: 'RED · major divergence',
  promptText: PROMPT_DIANA_DIRECTIVE,
  score: {
    fidelityScore: 42,
    fidelityBand: 'RED',
    fidelityComputedAt: '2026-05-20T20:35:48.213Z',
    fidelityProviderId: 'openai:whisper-1',
    fidelityBreakdown: {
      werScore: 30,
      werPct: 0.23,
      confScore: 64,
      minWordConfidence: -0.71,
      acousticScore: 50,
      acousticFlags: ['low_confidence_word', 'low_confidence_word'],
    },
    transcript: "Don't open the door. Stay right there.",
    transcriptDiff: [
      { type: 'match', promptWord: "Don't", transcribedWord: "Don't" },
      { type: 'match', promptWord: 'open', transcribedWord: 'open' },
      { type: 'sub', promptWord: 'that', transcribedWord: 'the' },
      { type: 'match', promptWord: 'door.', transcribedWord: 'door.' },
      { type: 'del', promptWord: 'I', transcribedWord: null },
      { type: 'del', promptWord: 'mean', transcribedWord: null },
      { type: 'del', promptWord: 'it.', transcribedWord: null },
      { type: 'match', promptWord: 'Stay', transcribedWord: 'Stay' },
      { type: 'sub', promptWord: 'where', transcribedWord: 'right' },
      { type: 'sub', promptWord: 'you', transcribedWord: 'there.' },
      { type: 'del', promptWord: 'are.', transcribedWord: null },
    ],
  },
};

/**
 * GRAY — scoring timed out or failed. Save & Submit unlocked.
 */
const grayState = {
  label: 'GRAY · scoring unavailable',
  promptText: PROMPT_CLEAN,
  score: {
    fidelityScore: null,
    fidelityBand: 'GRAY',
    fidelityComputedAt: '2026-05-20T20:36:11.000Z',
    fidelityProviderId: 'openai:whisper-1',
    fidelityBreakdown: null,
    transcript: null,
    transcriptDiff: null,
    errorReason: 'Scoring service timed out after 8 seconds.',
  },
};

/**
 * PENDING — score in flight. Optimistic preview band only. Save & Submit disabled.
 */
const pendingState = {
  label: 'PENDING · scoring in flight',
  promptText: PROMPT_CLEAN,
  score: {
    fidelityScore: null,
    fidelityBand: 'PENDING',
    fidelityComputedAt: null,
    fidelityProviderId: 'openai:whisper-1',
    fidelityBreakdown: null,
    transcript: null,
    transcriptDiff: null,
  },
};

/**
 * AMBER (long prompt) — single substitution near the end. Score 78.
 * Useful for confirming the diff view degrades gracefully across longer lines.
 */
const amberLongState = {
  label: 'AMBER · long-line near-miss',
  promptText: PROMPT_LONG,
  score: {
    fidelityScore: 78,
    fidelityBand: 'AMBER',
    fidelityComputedAt: '2026-05-20T20:37:02.184Z',
    fidelityProviderId: 'openai:whisper-1',
    fidelityBreakdown: {
      werScore: 80,
      werPct: 0.043,
      confScore: 84,
      minWordConfidence: -0.51,
      acousticScore: 50,
      acousticFlags: ['low_confidence_word', 'low_confidence_word'],
    },
    transcript:
      'The wind cut across the field and she pulled her coat tighter knowing the truck would not come back this side of dawn',
    transcriptDiff: [
      { type: 'match', promptWord: 'The', transcribedWord: 'The' },
      { type: 'match', promptWord: 'wind', transcribedWord: 'wind' },
      { type: 'match', promptWord: 'cut', transcribedWord: 'cut' },
      { type: 'match', promptWord: 'across', transcribedWord: 'across' },
      { type: 'match', promptWord: 'the', transcribedWord: 'the' },
      { type: 'match', promptWord: 'field', transcribedWord: 'field' },
      { type: 'match', promptWord: 'and', transcribedWord: 'and' },
      { type: 'match', promptWord: 'she', transcribedWord: 'she' },
      { type: 'match', promptWord: 'pulled', transcribedWord: 'pulled' },
      { type: 'match', promptWord: 'her', transcribedWord: 'her' },
      { type: 'match', promptWord: 'coat', transcribedWord: 'coat' },
      { type: 'match', promptWord: 'tighter,', transcribedWord: 'tighter' },
      { type: 'match', promptWord: 'knowing', transcribedWord: 'knowing' },
      { type: 'match', promptWord: 'the', transcribedWord: 'the' },
      { type: 'match', promptWord: 'truck', transcribedWord: 'truck' },
      { type: 'match', promptWord: 'would', transcribedWord: 'would' },
      { type: 'match', promptWord: 'not', transcribedWord: 'not' },
      { type: 'match', promptWord: 'come', transcribedWord: 'come' },
      { type: 'match', promptWord: 'back', transcribedWord: 'back' },
      { type: 'match', promptWord: 'this', transcribedWord: 'this' },
      { type: 'match', promptWord: 'side', transcribedWord: 'side' },
      { type: 'match', promptWord: 'of', transcribedWord: 'of' },
      { type: 'sub', promptWord: 'midnight.', transcribedWord: 'dawn' },
    ],
  },
};

const mockFidelityStates = {
  green: greenState,
  amber: amberState,
  amberLong: amberLongState,
  red: redState,
  gray: grayState,
  pending: pendingState,
};

export default mockFidelityStates;
